import {
  Dropdown as FDropdown,
  ICalloutProps,
  Icon,
  IDropdownOption,
  IDropdownProps as FIDropdownProps,
  IDropdownStyles,
  IIconStyles,
  IRawStyle,
  Label,
  Spinner,
  SpinnerSize,
  Stack,
} from '@fluentui/react';
import { forwardRef, ReactNode, Ref } from 'react';

import { IComponent, IconName, IStyleFunctionOrObject } from '../../models/components.models';
import { iconStylesCaretDown, iconStylesOption } from '../../themes/themes';
import { useThemeStyles } from '../../utils/hooks';
import { combineClassNames, singleOrArrayToArray } from '../../utils/utils';
import { BasicList } from '../BasicList/BasicList';
import { basicListStylesDropdown, IBasicListStyles } from '../BasicList/BasicList.styles';
import { Item } from '../Item/Item';
import { IItemStyles, itemStylesDropdown } from '../Item/Item.styles';
import { dropdownStylesDefault } from './Dropdown.styles';

const onRenderLoader = (loadingMessage: string) => () => {
  return (
    <Stack horizontal verticalAlign="center">
      <Spinner size={SpinnerSize.small} />
      <Label styles={{ root: { padding: '0 0 0 12px' } }}>{loadingMessage}</Label>
    </Stack>
  );
};

const onRenderOptionIcon = (option: IDropdownOption, icon?: ReactNode) => {
  return option ? (
    <div
      className="dropdown-option"
      style={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
    >
      {icon}
      <span className="dropdown-option__text">{option.text}</span>
    </div>
  ) : (
    <div></div>
  );
};

const onRenderSelectedItems = (
  options: IDropdownOption[] = [],
  onClose: (data: IDropdownOption, event: React.MouseEvent<unknown>) => void,
  selectedBasicListStyles?: IStyleFunctionOrObject<IBasicListStyles> | IStyleFunctionOrObject<IBasicListStyles>[],
  selectedItemStyles?: IStyleFunctionOrObject<IItemStyles> | IStyleFunctionOrObject<IItemStyles>[]
) => {
  return (
    <BasicList
      styles={[basicListStylesDropdown, ...singleOrArrayToArray(selectedBasicListStyles)]}
      idField="key"
      labelField="text"
      data={options}
      horizontal
      itemRenderer={(d: IDropdownOption) => (
        <Item
          styles={[itemStylesDropdown, ...singleOrArrayToArray(selectedItemStyles)]}
          hasClose
          data={d}
          idField="key"
          labelField="text"
          onClose={onClose}
        />
      )}
    />
  );
};

const getIcon = (iconStyles: Partial<IIconStyles>, iconName?: string, className?: string) =>
  !iconName ? null : <Icon className={className} iconName={iconName} styles={iconStyles}></Icon>;

export interface IDropdownProps extends Omit<FIDropdownProps, 'styles'>, IComponent<Partial<IDropdownStyles>> {
  iconName?: string;
  hasOptionIcon?: boolean;
  selectedBasicListStyles?: IStyleFunctionOrObject<IBasicListStyles> | IStyleFunctionOrObject<IBasicListStyles>[];
  selectedItemStyles?: IStyleFunctionOrObject<IItemStyles> | IStyleFunctionOrObject<IItemStyles>[];
  width?: number | string;
  preventDismissOnEventTypes?: string[];
  emptyMessage?: string;
  loadingMessage?: string;
}

export const Dropdown = forwardRef((props: IDropdownProps, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    styles,
    iconName,
    hasOptionIcon,
    selectedBasicListStyles,
    selectedItemStyles,
    width,
    loadingMessage,
    preventDismissOnEventTypes,
    emptyMessage = 'None available',
    ..._fProps
  } = props;
  const {
    label,
    onRenderTitle,
    onRenderOption,
    onRenderLabel,
    onRenderCaretDown,
    multiSelect,
    onChange,
    disabled,
    options,
    placeholder,
  } = props;

  const onRemoveItem = (option: IDropdownOption, event: React.MouseEvent<any>) => {
    if (onChange) {
      onChange(event, { ...option, selected: false });
    }
    event.stopPropagation();
  };

  const calloutProps: ICalloutProps =
    preventDismissOnEventTypes && preventDismissOnEventTypes.length
      ? {
          preventDismissOnEvent: (event) => preventDismissOnEventTypes.includes(event.type),
        }
      : {};
  const componentStyles: IRawStyle = width ? { dropdown: { width } } : {};
  const optionIconStyles = useThemeStyles<IIconStyles>(undefined, iconStylesOption);
  const caretDownIconStyles = useThemeStyles<IIconStyles>(undefined, iconStylesCaretDown);
  const fProps: FIDropdownProps = {
    ..._fProps,
    className: combineClassNames(className, multiSelect && 'multi-select'),
    styles: useThemeStyles<Partial<IDropdownStyles>>(dropdownStylesDefault, [
      ...(singleOrArrayToArray(styles) as any),
      componentStyles,
    ]),
    title: label,
    onRenderTitle: loadingMessage
      ? onRenderLoader(loadingMessage)
      : onRenderTitle
      ? onRenderTitle
      : multiSelect
      ? (options) => onRenderSelectedItems(options, onRemoveItem, selectedBasicListStyles, selectedItemStyles)
      : hasOptionIcon
      ? (options) =>
          options ? (
            onRenderOptionIcon(options[0], getIcon(optionIconStyles, options[0].data?.icon, 'dropdown-option__icon'))
          ) : (
            <div></div>
          )
      : undefined,
    onRenderOption: onRenderOption
      ? onRenderOption
      : hasOptionIcon
      ? (option) =>
          option ? (
            onRenderOptionIcon(option, getIcon(optionIconStyles, option.data?.icon, 'dropdown-option__icon'))
          ) : (
            <div></div>
          )
      : undefined,
    onRenderLabel: onRenderLabel
      ? onRenderLabel
      : iconName
      ? () => getIcon(caretDownIconStyles, IconName.Sort, 'dropdown-icon')
      : undefined,
    onRenderCaretDown: onRenderCaretDown ? onRenderCaretDown : () => getIcon(caretDownIconStyles, IconName.ChevronDown),
    options: multiSelect && loadingMessage ? options.map((o) => ({ ...o, disabled: true })) : options,
    calloutProps,
    disabled: Boolean(disabled || loadingMessage || options?.length === 0),
    placeholder: options?.length === 0 ? emptyMessage : placeholder,
  };

  return <FDropdown {...fProps} ref={ref} data-test={props['data-test'] || 'dropdown'} />;
});
